import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { messageFromUnknownApiError } from '../lib/mapApiUnknownError'
import { isAxiosError } from 'axios'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { ConfirmCancelModal } from '@/shared/components/ConfirmCancelModal'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import {
  Navigate,
  useBlocker,
  useNavigate,
  useParams,
} from 'react-router-dom'
import { WIZARD_CONTINUE_TO_TEAM_STATE } from '../lib/campaignWizardNavigation'
import { isCampaignLaunched } from '../lib/campaignResumeWizardPath'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useToast } from '@/shared/toast/ToastProvider'
import { useSessionStore } from '@/stores/useSessionStore'
import {
  useDeleteLoi,
  usePatchCampaign,
  useReorderLoi,
  useUpdateLoi,
} from '../api/campaign-mutations'
import { useCampaign } from '../api/campaign-queries'
import { CampaignWizardChrome } from '../components/CampaignWizardChrome'
import { LoiCard } from '../components/LoiCard'
import { LoiCardEdit } from '../components/LoiCardEdit'
import { LOI_TITLE_PATTERN } from '../components/LoiCardEdit.validation'
import {
  loiResetToolbarTooltip,
  loiSaveToolbarTooltip,
} from '../components/loiCardEditToolbarState'
import { LoiDeleteModal } from '../components/LoiDeleteModal'
import type { Campaign, LineOfInquiry } from '../types'

const GENERATING_TIMEOUT_MS = 60_000

function sortLois(lois: LineOfInquiry[] | null | undefined): LineOfInquiry[] {
  if (!Array.isArray(lois) || lois.length === 0) return []
  return [...lois].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}

function isValidLoiTitle(s: string): boolean {
  const t = s.trim()
  return t.length >= 5 && t.length <= 100 && LOI_TITLE_PATTERN.test(t)
}

function isValidLoiDescription(s: string): boolean {
  const t = s.trim()
  return (
    t.length >= 10 &&
    t.length <= 200 &&
    !/[\r\n]/.test(s)
  )
}

function titlesUniqueAmongOthers(
  title: string,
  loiId: string,
  all: LineOfInquiry[],
): boolean {
  const key = title.trim().toLowerCase()
  return !all.some(
    (l) => l.id !== loiId && l.title.trim().toLowerCase() === key,
  )
}

function useGeneratingTimedOut(
  waitingForLois: boolean,
  campaignId: string | undefined,
) {
  const [generatingTimedOut, setGeneratingTimedOut] = useState(false)
  useEffect(() => {
    if (!waitingForLois) {
      setGeneratingTimedOut(false)
      return
    }
    const t = globalThis.setTimeout(() => {
      setGeneratingTimedOut(true)
    }, GENERATING_TIMEOUT_MS)
    return () => {
      globalThis.clearTimeout(t)
    }
  }, [waitingForLois, campaignId])
  return generatingTimedOut
}

function useBeforeUnloadWhenDirty(dirty: boolean, editingLoiId: string | null) {
  useEffect(() => {
    if (!dirty || !editingLoiId) return
    const fn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    globalThis.addEventListener('beforeunload', fn)
    return () => globalThis.removeEventListener('beforeunload', fn)
  }, [dirty, editingLoiId])
}

function useEditClickAway(
  editingLoiId: string | null,
  dirty: boolean,
  deleteTarget: LineOfInquiry | null,
  editCardRef: RefObject<HTMLDivElement | null>,
  onDirtyClickAway: () => void,
  onCleanClickAway: () => void,
) {
  useEffect(() => {
    if (!editingLoiId) return
    const onMouseDown = (e: MouseEvent) => {
      if (deleteTarget) return
      if (!(e.target instanceof Node)) return
      if (e.target instanceof Element && e.target.closest('[role="dialog"]')) {
        return
      }
      if (editCardRef.current?.contains(e.target)) return
      if (dirty) {
        onDirtyClickAway()
      } else {
        onCleanClickAway()
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [
    editingLoiId,
    dirty,
    deleteTarget,
    editCardRef,
    onDirtyClickAway,
    onCleanClickAway,
  ])
}

function renderLoiPhaseGate(params: {
  isLoading: boolean
  isFetching: boolean
  campaign: Campaign | undefined
  campaignId: string
  waitingForLois: boolean
  generatingTimedOut: boolean
  sortedLois: LineOfInquiry[]
  onRetryRefetch: () => void
}): ReactNode | null {
  const {
    isLoading,
    isFetching,
    campaign,
    campaignId,
    waitingForLois,
    generatingTimedOut,
    sortedLois,
    onRetryRefetch,
  } = params

  if (isLoading || (isFetching && !campaign)) {
    return (
      <CampaignWizardChrome
        wizardStep={3}
        totalWizardSteps={4}
        activePhaseIndex={2}
        campaignId={campaignId}
      >
        <Stack spacing={2} data-testid="loi-loading-skeleton">
          <Skeleton variant="rounded" height={120} />
          <Skeleton variant="rounded" height={120} />
        </Stack>
      </CampaignWizardChrome>
    )
  }

  if (!campaign) {
    return <Navigate to="/campaigns" replace />
  }

  // Launched campaigns leave the wizard; do not force Step 4 → Team when the
  // Admin intentionally returns to Step 3 (US186 / US-CS-04 back navigation).
  if (isCampaignLaunched(campaign)) {
    return <Navigate to={`/campaigns/${campaignId}`} replace />
  }

  if (campaign.wizardStep < 3) {
    return (
      <Navigate
        to={`/campaigns/${campaignId}/goal-analysis`}
        replace
      />
    )
  }

  if (waitingForLois && !generatingTimedOut) {
    return (
      <CampaignWizardChrome
        wizardStep={3}
        totalWizardSteps={4}
        activePhaseIndex={2}
        campaignId={campaignId}
      >
        <Stack
          alignItems="center"
          spacing={2}
          sx={{ py: 6 }}
          data-testid="loi-generating"
        >
          <CircularProgress aria-label="Generating lines of inquiry" />
          <Typography variant="body1" color="text.secondary" textAlign="center">
            Generating your Lines of Inquiry…
          </Typography>
        </Stack>
      </CampaignWizardChrome>
    )
  }

  if (waitingForLois && generatingTimedOut) {
    return (
      <CampaignWizardChrome
        wizardStep={3}
        totalWizardSteps={4}
        activePhaseIndex={2}
        campaignId={campaignId}
      >
        <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
          <Typography color="error" textAlign="center">
            This is taking longer than expected. Check your connection or try
            again.
          </Typography>
          <Button variant="contained" onClick={onRetryRefetch}>
            Retry
          </Button>
        </Stack>
      </CampaignWizardChrome>
    )
  }

  if (sortedLois.length === 0) {
    return (
      <CampaignWizardChrome
        wizardStep={3}
        totalWizardSteps={4}
        activePhaseIndex={2}
        campaignId={campaignId}
      >
        <Typography color="text.secondary">
          No lines of inquiry were returned for this campaign.
        </Typography>
      </CampaignWizardChrome>
    )
  }

  return null
}

type SortableLoiRowProps = {
  readonly loi: LineOfInquiry
  readonly rankIndex: number
  readonly editingLoiId: string | null
  readonly editSurfaceRef: RefObject<HTMLDivElement | null>
  readonly editDraftTitle: string
  readonly editDraftDescription: string
  readonly clickAwayMessage: string | null
  readonly onEditDraftTitle: (v: string) => void
  readonly onEditDraftDescription: (v: string) => void
  readonly onEdit: (id: string) => void
  readonly onSave: () => void
  readonly onReset: () => void
  readonly onDeleteClick: () => void
  readonly saveDisabled: boolean
  readonly resetDisabled: boolean
  readonly deleteDisabled: boolean
  readonly saveTooltip: string
  readonly resetTooltip: string
  readonly titleAuxError: string
  readonly isSaving: boolean
}

function SortableLoiRow({
  loi,
  rankIndex,
  editingLoiId,
  editSurfaceRef,
  editDraftTitle,
  editDraftDescription,
  clickAwayMessage,
  onEditDraftTitle,
  onEditDraftDescription,
  onEdit,
  onSave,
  onReset,
  onDeleteClick,
  saveDisabled,
  resetDisabled,
  deleteDisabled,
  saveTooltip,
  resetTooltip,
  titleAuxError,
  isSaving,
}: SortableLoiRowProps) {
  const editLocked = editingLoiId !== null && editingLoiId !== loi.id
  const isEditing = editingLoiId === loi.id

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: loi.id,
      disabled: editingLoiId !== null,
    })

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <Box ref={setNodeRef} style={style} {...attributes} sx={{ mb: 2 }}>
      {isEditing ? (
        <LoiCardEdit
          surfaceRef={editSurfaceRef}
          loi={loi}
          rankIndex={rankIndex}
          title={editDraftTitle}
          description={editDraftDescription}
          onChangeTitle={onEditDraftTitle}
          onChangeDescription={onEditDraftDescription}
          onSave={onSave}
          onReset={onReset}
          onDeleteClick={onDeleteClick}
          saveDisabled={saveDisabled}
          resetDisabled={resetDisabled}
          deleteDisabled={deleteDisabled}
          saveTooltip={saveTooltip}
          resetTooltip={resetTooltip}
          titleAuxError={titleAuxError}
          isSaving={isSaving}
          clickAwayMessage={clickAwayMessage}
        />
      ) : (
        <LoiCard
          loi={loi}
          rankIndex={rankIndex}
          onEdit={() => onEdit(loi.id)}
          editDisabled={editLocked}
          dragDisabled={editingLoiId !== null}
          dragHandleProps={listeners ?? {}}
          isDragging={isDragging}
        />
      )}
    </Box>
  )
}

/** US-CS-03 — Lines of inquiry list, edit, reorder, delete (ADMIN). */
export function CampaignLinesOfInquiryPage() {
  const role = useSessionStore((s) => s.user?.role)
  const { campaignId = '' } = useParams<{ campaignId: string }>()
  const navigate = useNavigate()
  const { showToast } = useToast()
  useDocumentTitle('Lines of inquiry')

  const { data: campaign, isLoading, refetch, isFetching } =
    useCampaign(campaignId)

  const updateLoi = useUpdateLoi()
  const deleteLoi = useDeleteLoi()
  const reorderLoi = useReorderLoi()
  const patchCampaign = usePatchCampaign()
  const leaveRef = useRef(false)

  const handleContinueToTeam = useCallback(async () => {
    leaveRef.current = true
    try {
      const updated = await patchCampaign.mutateAsync({
        campaignId,
        body: { wizardStep: 4, status: 'lines_of_inquiry' },
      })
      // Push (not replace) so Browser Back returns to Step 3 with LOI edits intact.
      navigate(`/campaigns/${campaignId}/team-and-launch`, {
        state: { [WIZARD_CONTINUE_TO_TEAM_STATE]: true, campaign: updated },
      })
    } catch (e: unknown) {
      showToast(
        messageFromUnknownApiError(e, 'Could not continue to team'),
        'error',
      )
    } finally {
      queueMicrotask(() => {
        leaveRef.current = false
      })
    }
  }, [campaignId, navigate, patchCampaign, showToast])

  const [editingLoiId, setEditingLoiId] = useState<string | null>(null)
  const [editDraftTitle, setEditDraftTitle] = useState('')
  const [editDraftDescription, setEditDraftDescription] = useState('')
  const [clickAwayMessage, setClickAwayMessage] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<LineOfInquiry | null>(null)
  const [navBlockOpen, setNavBlockOpen] = useState(false)

  const editCardRef = useRef<HTMLDivElement | null>(null)

  const sortedLois = useMemo(
    () => sortLois(campaign?.linesOfInquiry ?? null),
    [campaign?.linesOfInquiry],
  )

  const waitingForLois =
    campaign &&
    campaign.wizardStep >= 3 &&
    (campaign.linesOfInquiry === null ||
      !Array.isArray(campaign.linesOfInquiry))

  const generatingTimedOut = useGeneratingTimedOut(
    Boolean(waitingForLois),
    campaign?.id,
  )

  const dirty = useMemo(() => {
    if (!editingLoiId || !campaign?.linesOfInquiry) return false
    const cur = sortedLois.find((l) => l.id === editingLoiId)
    if (!cur) return false
    return (
      editDraftTitle.trim() !== cur.title.trim() ||
      editDraftDescription.trim() !== cur.description.trim()
    )
  }, [
    editingLoiId,
    editDraftTitle,
    editDraftDescription,
    sortedLois,
    campaign?.linesOfInquiry,
  ])

  useBeforeUnloadWhenDirty(dirty, editingLoiId)

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !leaveRef.current &&
      Boolean(editingLoiId) &&
      currentLocation.pathname !== nextLocation.pathname,
  )

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setNavBlockOpen(true)
    }
  }, [blocker.state])

  const notifyDirtyClickAway = useCallback(() => {
    setClickAwayMessage(
      'Please save or reset your changes before exiting edit mode',
    )
  }, [])

  const notifyCleanClickAway = useCallback(() => {
    setEditingLoiId(null)
    setClickAwayMessage(null)
  }, [])

  useEditClickAway(
    editingLoiId,
    dirty,
    deleteTarget,
    editCardRef,
    notifyDirtyClickAway,
    notifyCleanClickAway,
  )

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const startEdit = useCallback(
    (id: string) => {
      const loi = sortedLois.find((l) => l.id === id)
      if (!loi) return
      setEditingLoiId(id)
      setEditDraftTitle(loi.title)
      setEditDraftDescription(loi.description)
      setClickAwayMessage(null)
    },
    [sortedLois],
  )

  const saveEdit = useCallback(async () => {
    if (!editingLoiId || !campaignId) return
    const titleOk = isValidLoiTitle(editDraftTitle)
    const descOk = isValidLoiDescription(editDraftDescription)
    if (!titleOk || !descOk) return
    if (
      !titlesUniqueAmongOthers(
        editDraftTitle,
        editingLoiId,
        sortedLois,
      )
    ) {
      showToast('Each LOI title must be unique in this campaign', 'error')
      return
    }
    try {
      await updateLoi.mutateAsync({
        campaignId,
        loiId: editingLoiId,
        body: {
          title: editDraftTitle.trim(),
          description: editDraftDescription.trim(),
        },
      })
      setEditingLoiId(null)
      setClickAwayMessage(null)
      showToast('LOI updated successfully', 'success')
    } catch (e: unknown) {
      const msg = isAxiosError(e)
        ? String(
            (e.response?.data as { message?: string } | undefined)?.message ??
              e.message,
          )
        : 'Could not update LOI'
      showToast(msg, 'error')
    }
  }, [
    editingLoiId,
    campaignId,
    editDraftTitle,
    editDraftDescription,
    sortedLois,
    updateLoi,
    showToast,
  ])

  const resetEdit = useCallback(() => {
    if (!editingLoiId) return
    const loi = sortedLois.find((l) => l.id === editingLoiId)
    if (!loi) return
    setEditDraftTitle(loi.title)
    setEditDraftDescription(loi.description)
    setClickAwayMessage(null)
  }, [editingLoiId, sortedLois])

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget || !campaignId) return
    try {
      await deleteLoi.mutateAsync({
        campaignId,
        loiId: deleteTarget.id,
      })
      if (editingLoiId === deleteTarget.id) {
        setEditingLoiId(null)
        setClickAwayMessage(null)
      }
      setDeleteTarget(null)
      showToast('LOI deleted successfully', 'success')
    } catch (e: unknown) {
      const msg = isAxiosError(e)
        ? String(
            (e.response?.data as { message?: string } | undefined)?.message ??
              e.message,
          )
        : 'Could not delete LOI'
      showToast(msg, 'error')
    }
  }, [deleteTarget, campaignId, deleteLoi, editingLoiId, showToast])

  const reorderOnDragEnd = useCallback(
    async (event: DragEndEvent) => {
      if (editingLoiId) return
      const { active, over } = event
      if (!over || active.id === over.id || !campaignId) return
      const oldIndex = sortedLois.findIndex((l) => l.id === active.id)
      const newIndex = sortedLois.findIndex((l) => l.id === over.id)
      if (oldIndex < 0 || newIndex < 0) return
      const next = arrayMove(sortedLois, oldIndex, newIndex)
      const orderedIds = next.map((l) => l.id)
      try {
        await reorderLoi.mutateAsync({ campaignId, orderedIds })
      } catch (e: unknown) {
        const msg = isAxiosError(e)
          ? String(
              (e.response?.data as { message?: string } | undefined)
                ?.message ?? e.message,
            )
          : 'Could not reorder LOIs'
        showToast(msg, 'error')
      }
    },
    [sortedLois, campaignId, editingLoiId, reorderLoi, showToast],
  )

  const handleRetryRefetch = useCallback(() => {
    refetch().catch(() => {})
  }, [refetch])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      reorderOnDragEnd(event).catch(() => {})
    },
    [reorderOnDragEnd],
  )

  const handleSaveEditClick = useCallback(() => {
    saveEdit().catch(() => {})
  }, [saveEdit])

  const handleConfirmDeleteClick = useCallback(() => {
    confirmDelete().catch(() => {})
  }, [confirmDelete])

  const editingLoi = editingLoiId
    ? sortedLois.find((l) => l.id === editingLoiId)
    : undefined

  const duplicateTitle =
    Boolean(editingLoi) &&
    !titlesUniqueAmongOthers(
      editDraftTitle,
      editingLoi!.id,
      sortedLois,
    )

  const saveDisabled =
    !editingLoi ||
    !dirty ||
    !isValidLoiTitle(editDraftTitle) ||
    !isValidLoiDescription(editDraftDescription) ||
    duplicateTitle

  const resetDisabled = !dirty || updateLoi.isPending

  const saveTooltip = loiSaveToolbarTooltip(
    saveDisabled,
    dirty,
    editDraftTitle,
    editDraftDescription,
    duplicateTitle,
  )

  const resetTooltip = loiResetToolbarTooltip(
    resetDisabled,
    updateLoi.isPending,
  )

  const titleAuxError = duplicateTitle
    ? 'Each LOI title must be unique in this campaign'
    : ''

  const cancelNavigationBlock = useCallback(() => {
    setNavBlockOpen(false)
    if (blocker.state === 'blocked') {
      blocker.reset()
    }
  }, [blocker])

  const confirmLeaveWhileEditing = useCallback(() => {
    setNavBlockOpen(false)
    if (blocker.state === 'blocked') {
      if (dirty) {
        blocker.reset()
        return
      }
      leaveRef.current = true
      setEditingLoiId(null)
      blocker.proceed()
      queueMicrotask(() => {
        leaveRef.current = false
      })
    }
  }, [blocker, dirty])

  if (role !== 'ADMIN') {
    return <Navigate to="/unauthorized" replace />
  }

  const phaseGate = renderLoiPhaseGate({
    isLoading,
    isFetching,
    campaign,
    campaignId,
    waitingForLois: Boolean(waitingForLois),
    generatingTimedOut,
    sortedLois,
    onRetryRefetch: handleRetryRefetch,
  })
  if (phaseGate !== null) {
    return <>{phaseGate}</>
  }

  const stepperLocked = editingLoiId !== null

  return (
    <>
      <CampaignWizardChrome
        wizardStep={3}
        totalWizardSteps={4}
        activePhaseIndex={2}
        campaignId={campaignId}
        stepperNavigationLocked={stepperLocked}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, mb: 1, color: '#0f172a' }}
          >
            Lines of inquiry
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Drag to set priority. The first card is your primary LOI for the
            campaign.
          </Typography>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortedLois.map((l) => l.id)}
              strategy={verticalListSortingStrategy}
            >
              {sortedLois.map((loi, index) => (
                <SortableLoiRow
                  key={loi.id}
                  loi={loi}
                  rankIndex={index}
                  editingLoiId={editingLoiId}
                  editSurfaceRef={editCardRef}
                  editDraftTitle={editDraftTitle}
                  editDraftDescription={editDraftDescription}
                  clickAwayMessage={
                    editingLoiId === loi.id ? clickAwayMessage : null
                  }
                  onEditDraftTitle={(v) => {
                    setEditDraftTitle(v)
                    setClickAwayMessage(null)
                  }}
                  onEditDraftDescription={(v) => {
                    setEditDraftDescription(v)
                    setClickAwayMessage(null)
                  }}
                  onEdit={startEdit}
                  onSave={handleSaveEditClick}
                  onReset={resetEdit}
                  onDeleteClick={() => setDeleteTarget(loi)}
                  saveDisabled={saveDisabled}
                  resetDisabled={resetDisabled}
                  deleteDisabled={sortedLois.length <= 1}
                  saveTooltip={saveTooltip}
                  resetTooltip={resetTooltip}
                  titleAuxError={titleAuxError}
                  isSaving={updateLoi.isPending}
                />
              ))}
            </SortableContext>
          </DndContext>

          <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
            <Button
              variant="text"
              color="inherit"
              onClick={() =>
                navigate(`/campaigns/${campaignId}/goal-analysis`)
              }
              disabled={stepperLocked}
              sx={{ color: '#4a5565', textTransform: 'none' }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleContinueToTeam()}
              disabled={stepperLocked || patchCampaign.isPending}
            >
              {patchCampaign.isPending ? 'Continuing…' : 'Continue to team'}
            </Button>
          </Stack>
        </Box>
      </CampaignWizardChrome>

      <LoiDeleteModal
        open={Boolean(deleteTarget)}
        isDeleting={deleteLoi.isPending}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDeleteClick}
      />

      <ConfirmCancelModal
        open={navBlockOpen}
        iconVariant={dirty ? 'caution' : 'cancel'}
        headline={dirty ? 'Unsaved changes' : 'Leave lines of inquiry?'}
        bodyLines={
          dirty
            ? ['Save or reset your LOI changes before leaving this page.', '']
            : ['You are editing a line of inquiry.', 'Leave anyway?']
        }
        stayLabel="Stay"
        confirmLabel={dirty ? 'Got it' : 'Leave'}
        confirmTone={dirty ? 'primary' : 'danger'}
        onStay={cancelNavigationBlock}
        onConfirm={confirmLeaveWhileEditing}
        dialogTestId="loi-nav-block-dialog"
      />
    </>
  )
}
